import type { TProvisioningStepStatus } from '../types/provisioning.type';

export const CHAT_EVENTS = {
  PLATFROM_TO_CORE_EVENTS: {
    NEW_AGENT_MESSAGE: 'newAgentMessage',
    NEW_AGENT_NOTE: 'newAgentNote',
    CLOSE_CONVERSATION: 'handoverToBotByAgent',
    AGENT_TYPING: 'agentTyping',
    AGENT_READ_NOTIFICATION: 'agentReadNotification',
    NEW_SYSTEM_MESSAGE: 'newSystemMessage',
  },
  CORE_TO_PLATFORM_EVENTS: {
    NEW_USER: 'newUser',
    NEW_USER_MESSAGE: 'newUserMessage',
    NEW_CHAT_ACTIVITY_STATUS: 'newChatActivityStatus',
    NEW_USER_NOTIFICATION: 'newUserNotification',
    USER_TYPING: 'userTyping', // TODO: Needs to be implemented on webclient, other platforms do not support
    HANDOVER_TO_AGENT_OR_INBOX: 'passToHandover',
    CLOSE_CONVERSATION: 'closeConversation',
    HANDOVER_TO_QUEUE: 'assignedToQueue',
  },
} as const;

export const WEB_SCRAPER_EVENTS = {
  BACKEND_TO_PLATFORM: {
    PROGRESS: 'webScraper:progress',
    COMPLETED: 'webScraper:completed',
    ERROR: 'webScraper:error',
  },
} as const;

export const UPLOAD_DOCUMENTS_EVENTS = {
  BACKEND_TO_PLATFORM: {
    PROGRESS: 'upload-documents:progress',
    COMPLETED: 'upload-documents:completed',
    ERROR: 'upload-documents:error',
  },
  PLATFORM_TO_BACKEND: {
    START_UPLOAD: 'upload-documents:start',
  },
} as const;

export const ARTICLE_ARCHITECT_EVENTS = {
  BACKEND_TO_PLATFORM: {
    PROGRESS: 'article-architect:progress',
    COMPLETED: 'article-architect:completed',
    ERROR: 'article-architect:error',
    CONFLICT_SCAN_PROGRESS: 'article-architect:conflict-scan-progress',
    CONFLICT_SCAN_COMPLETED: 'article-architect:conflict-scan-completed',
    // * Terminal events for the one-time TopDesk YTD incident pull. Kept separate
    // * from COMPLETED/ERROR because the pull generates no articles - reusing the
    // * generation events would make the frontend report articles that never existed.
    TOPDESK_PULL_COMPLETED: 'article-architect:topdesk-pull-completed',
    TOPDESK_PULL_ERROR: 'article-architect:topdesk-pull-error',
    // * Carries the heartbeat progress count while a TopDesk YTD incident
    // * pull is still running.
    TOPDESK_PULL_PROGRESS: 'article-architect:topdesk-pull-progress',
    // * Estimate-ready event for the manual TopDesk draft-generation flow. Emitted
    // * once phase A (ingest, embed, cluster, dry-run count) finishes, so a
    // * superAdmin can confirm the spend before phase B generates any drafts.
    TOPDESK_GENERATION_ESTIMATE:
      'article-architect:topdesk-generation-estimate',
  },
  PLATFORM_TO_BACKEND: {
    START_GENERATION: 'article-architect:start',
    START_BCC_BATCH: 'article-architect:bcc-start',
    START_TOPDESK_PULL: 'article-architect:topdesk-pull-start',
    // * Manual "Generate draft articles" entry point. A superAdmin runs phase A
    // * only (estimate); a regular user runs the full pipeline straight through.
    // * The role split is decided server-side, never from a client flag.
    START_TOPDESK_GENERATION: 'article-architect:topdesk-generation-start',
    // * SuperAdmin-only confirm that runs phase B on the estimate's runId.
    CONFIRM_TOPDESK_GENERATION: 'article-architect:topdesk-generation-confirm',
    // * SuperAdmin-only cancel for the estimate leg (phase-A embedding) only.
    // * Generation is not cancellable, so this never affects a confirmed run.
    CANCEL_TOPDESK_GENERATION: 'article-architect:topdesk-generation-cancel',
  },
} as const;

export const PROVISIONING_EVENTS = {
  BACKEND_TO_PLATFORM: {
    STEP: 'provisioning:step',
    DONE: 'provisioning:done',
    FAILED: 'provisioning:failed',
  },
} as const;

// * Narrow subset the pipeline emits per step (starting, done, or failed); the
// * persisted DB step status (TProvisioningStepStatus) additionally covers the
// * rollback states, which are never emitted live.
export type TProvisioningStepEventStatus = Extract<
  TProvisioningStepStatus,
  'pending' | 'done' | 'failed'
>;

export type TProvisioningStepEvent = {
  companyId: string;
  step: number;
  name: string;
  status: TProvisioningStepEventStatus;
  detail?: string;
};

export type TProvisioningDoneEvent = {
  companyId: string;
  identifier: string;
};

export type TProvisioningFailedEvent = {
  companyId: string;
  step: number;
  name: string;
  error: string;
  rolledBack: boolean;
};

export const NOTIFICATION_CENTER_EVENTS = {
  BACKEND_TO_PLATFORM: {
    // * Emitted when a new item enters a pending-action state (e.g. KA enters awaiting-review).
    COUNT_ADDED: 'notification-center:count-added',
    // * Emitted when a pending item is resolved (reviewed, published, or deleted).
    COUNT_CLEARED: 'notification-center:count-cleared',
  },
} as const;

// * Payload shape shared by both COUNT_ADDED and COUNT_CLEARED.
export type TNotificationCountEventPayload = {
  type: 'trainAi' | 'knowledgeArticle';
};

export type WebsocketDenyReason = 'Unauthorized' | 'RateLimit';
